import { ActionEntityType, ImportColumnType, ImportSchemaColumn } from '@pmp/api';
import {
  AdditionalFields,
  ImportTableDataRow,
  TableRowBase,
  TableColumnFormat,
  TableColumns,
  TableEditableCells,
  transformToTableColumns,
} from '@ohpen/enso-ui';

import { getColumnDecimals } from '@pmp/utils';

import { baseRateChangeColumns } from '../baseRates/tableConfig';
import { ercProfilesFlatColumns } from '../ercProfiles/tableConfig';
import { exchangeRateChangeColumns } from '../exchangeRates/tableConfig';
import { feesFlatColumns } from '../fees/tableConfig';
import { productsColumns } from '../products/tableConfig';

const SELECTED_ROWS_LABELS: Partial<Record<ActionEntityType, string>> = {
  [ActionEntityType.PRODUCTS]: 'pmp.products.labels.xProductsSelected',
  [ActionEntityType.BASE_RATES]: 'pmp.baseRates.labels.xBaseRatesSelected',
  [ActionEntityType.ERC_PROFILES]: 'pmp.erc.labels.xErcProfilesSelected',
  [ActionEntityType.FEE_PROFILES]: 'pmp.fees.labels.xFeeProfilesSelected',
  [ActionEntityType.FEES]: 'pmp.fees.labels.xFeesSelected',
  [ActionEntityType.EXCHANGE_RATES]: 'pmp.exchangeRates.labels.xExchangeRatesSelected',
  [ActionEntityType.EXCLUSIVITY_SETS]: 'pmp.exclusiveProducts.labels.xExclusivitySetsSelected',
  [ActionEntityType.FIRMS]: 'firms.labels.xFirmsSelected',
};

export const getSelectedRowsLabel = (importEntity: ActionEntityType): string =>
  SELECTED_ROWS_LABELS[importEntity] ?? 'enso.labels.xItemsSelected';

// The columns whose decimals an import of each entity shows, keyed as the import schema keys its fields.
type FormattedColumn = { dataIndex?: unknown; options?: { format?: TableColumnFormat | null } };

const IMPORT_COLUMNS: Partial<Record<ActionEntityType, readonly FormattedColumn[]>> = {
  [ActionEntityType.PRODUCTS]: productsColumns,
  [ActionEntityType.BASE_RATES]: baseRateChangeColumns,
  [ActionEntityType.ERC_PROFILES]: ercProfilesFlatColumns,
  [ActionEntityType.FEES]: feesFlatColumns,
  [ActionEntityType.EXCHANGE_RATES]: exchangeRateChangeColumns,
};

/** Decimals each import field shows, the same as its column. */
export const getImportDecimalScales = (importEntity: ActionEntityType): Readonly<Record<string, number>> =>
  (IMPORT_COLUMNS[importEntity] ?? []).reduce<Record<string, number>>((scales, column) => {
    const decimals = getColumnDecimals(column.options?.format);
    if (typeof column.dataIndex === 'string' && decimals !== undefined) scales[column.dataIndex] = decimals;
    return scales;
  }, {});

export const getEditableCells = <RowData>(columnKeys: string[], tableData: ImportTableDataRow<RowData>[]): TableEditableCells =>
  tableData?.reduce((obj, row) => {
    const editableColumns = columnKeys.reduce(
      (acc, key) => {
        if (typeof key === 'string') {
          acc[key] = true;
        }
        return acc;
      },
      {} as { [key: string]: boolean },
    );
    return { ...obj, [row.rowNumber]: editableColumns };
  }, {}) ?? {};

export const getAllTableColumns = <RowData>(
  schema: ImportSchemaColumn[],
  additionalFields: AdditionalFields,
): TableColumns<TableRowBase<RowData>> => {
  const typesMapping: Record<ImportColumnType, TableColumnFormat['type']> = {
    [ImportColumnType.ENUMERATION]: 'valueList',
    [ImportColumnType.MULTI_VALUE_ENUMERATION]: 'multiValueList',
    [ImportColumnType.MULTI_VALUE_STRING]: 'multiTag',
    [ImportColumnType.BOOLEAN]: 'boolean',
    [ImportColumnType.DATE]: 'date',
    [ImportColumnType.NUMBER]: 'number',
    [ImportColumnType.STRING]: 'text',
  };
  const columns = schema.map(column => ({
    key: column.key,
    dataIndex: column.key,
    title: column.label,
    options: {
      format: { type: typesMapping[column.type] },
      listName: column.listName ?? undefined,
    },
  }));

  if (!additionalFields || !Object.keys(additionalFields).length) return columns;

  const additionalFieldsColumns = transformToTableColumns(additionalFields) as TableColumns<TableRowBase<RowData>>;

  return [...columns, ...additionalFieldsColumns];
};
